import React, { memo, useContext, useEffect, useMemo } from 'react';
import { useContributorFeedbackStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { FeedbackType, transformWorkerCommentsToUnifiedFeedback } from '@scale/shared/types/chat';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import _ from 'lodash';

interface ContributorFeedbackWrapperProps {
  className?: string;
  children: React.ReactNode;
}

const ContributorFeedbackWrapper: React.FC<ContributorFeedbackWrapperProps> = memo(props => {
  const { className, children } = props;
  const setAllFeedback = useContributorFeedbackStore(s => s.setFullFeedbackState);
  const feedbackList = useContributorFeedbackStore(s => s.feedback);

  const taskContext = useContext(ChatTaskContext);
  const workerComments = useWorkerCommentsStore(state => state.workerComments);

  const [, allAutomatedFeedback] = useMemo(
    () =>
      _.partition(
        feedbackList,
        feedbackItem => feedbackItem.type === FeedbackType.HumanReviewerFeedback,
      ),
    [feedbackList],
  );

  /**
   * TODO: We don't need this anymore, but keeping it here for now in case we need it again.
   */
  // const allUnaddressedWorkerFeedback = useMemo(
  //   () =>
  //     allWorkerFeedback.filter(({ action, author }) => !action && taskContext.workerId !== author),
  //   [allWorkerFeedback, taskContext.workerId],
  // );

  // const allUnaddressedAutomatedFeedback = useMemo(
  //   // Automated feedback will always have an undefined `author` field
  //   () => allAutomatedFeedback.filter(({ action }) => !action),
  //   [allAutomatedFeedback],
  // );

  /**
   * Listen for changes to worker comments, transforms them into unified feedback, and updates
   * the unified feedback store with the updated feedback.
   */
  useEffect(() => {
    if (!taskContext.enableUnifiedFeedback) {
      return;
    }

    const updatedWorkerFeedback = transformWorkerCommentsToUnifiedFeedback(workerComments);

    setAllFeedback([...allAutomatedFeedback, ...updatedWorkerFeedback]);

    // Lint disabled here because if we add `allAutomatedFeedback` as a dependency, then the useEffect
    // will infinitely loop.

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerComments]);

  /**
   * TODO: Should be a better version than the above useEffect, but doesn't seem to run the callback
   * on mount. Need to investigate further.
   *
   * `useLifecycle` would be ideal here, but its `unmount` function doesn't have scope access to the
   * `unsubscribe` function returned by `useWorkerCommentsStore.subscribe`.
   */
  // useEffect(() => {
  //   if (!taskContext.enableUnifiedFeedback) {
  //     return;
  //   }
  //   const unsubscribe = useWorkerCommentsStore.subscribe(
  //     state => state.workerComments,
  //     (_prevWorkerComments, newWorkerComments) => {
  //       const updatedWorkerFeedback = transformWorkerCommentsToUnifiedFeedback(newWorkerComments);
  //       setAllFeedback([...allAutomatedFeedback, ...updatedWorkerFeedback]);
  //     },
  //     { fireImmediately: true },
  //   );

  //   return () => unsubscribe();

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [taskContext.enableUnifiedFeedback]);

  return <div className={className}>{children}</div>;
});

ContributorFeedbackWrapper.displayName = 'ContributorFeedbackWrapper';

export default ContributorFeedbackWrapper;
